import { useState, useEffect, useCallback, useRef, Suspense, lazy } from "react";
import {
  Sparkles,
  Check,
  X,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  BookOpen,
  Eye,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { ProblemText } from "./ProblemText";
import { problemTextToPreview } from "./problem-text";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ProblemImages } from "./ProblemImages";
import { qbFetch, invalidateQBCache, skillsUrl } from "./qb-cache";

const SurveyRenderer = lazy(() => import("./SurveyRenderer"));



function EvalBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${
        ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
      }`}
    >
      {ok ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />}{" "}
      {label}
    </span>
  );
}

function VerdictIcon({ verdict }: { verdict: string }) {
  if (verdict === "pass" || verdict === "evaluated")
    return <CheckCircle className="h-4 w-4 text-emerald-500" />;
  if (verdict === "fail" || verdict === "rejected")
    return <XCircle className="h-4 w-4 text-red-500" />;
  return <AlertCircle className="h-4 w-4 text-amber-500" />;
}

interface GeneratedProblem {
  id: string;
  standard_code: string;
  skill_name: string;
  question_text: string;
  question_type: string;
  difficulty_level: string;
  status: string;
  answer_choices: string[] | null;
  correct_answer: string | null;
  images: string[] | null;
  surveyjs_config: Record<string, unknown> | null;
  eval_result: {
    math: boolean;
    standard: boolean;
    difficulty: boolean;
    originality: number;
    verdict: string;
  } | null;
}

interface ReferenceProblem {
  id: string;
  question_text: string;
  question_type: string;
  difficulty_level: string;
  standard_code: string;
  answer_choices: string[] | null;
  correct_answer: string | null;
  source: string;
}

interface RunStatus {
  run: {
    id: string;
    status: string;
    count_requested: number;
    count_generated: number;
    count_failed: number;
  };
  problems: GeneratedProblem[];
  reference_problem: ReferenceProblem | null;
}

interface Standard {
  ccss_code: string;
  description: string;
  grade: string;
  domain: string | null;
}

interface SkillOption {
  id: string;
  standard_code: string;
  grade: string;
  skill_name: string;
  problem_count: number;
}

// Module-level cache: survives tab switches (unmount/remount) but resets on page reload
let _cachedRunId: string | null = null;
let _cachedRunStatus: RunStatus | null = null;
let _cachedDecisions: Record<string, "approved" | "rejected"> = {};
let _cachedGenerating: boolean = false;
let _cachedStandards: Standard[] = [];
let _cachedStandardCode: string = "";
let _cachedSkills: SkillOption[] = [];
let _cachedSkillName: string = "";

export function GenerationEngine() {
  const [standards, setStandards] = useState<Standard[]>(_cachedStandards);
  const [standardsLoading, setStandardsLoading] = useState(_cachedStandards.length === 0);
  const [standardCode, setStandardCode] = useState(_cachedStandardCode);
  const [standardSearch, setStandardSearch] = useState(_cachedStandardCode);
  const [standardOpen, setStandardOpen] = useState(false);
  const standardRef = useRef<HTMLDivElement>(null);

  const [skills, setSkills] = useState<SkillOption[]>(_cachedSkills);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillName, setSkillName] = useState(_cachedSkillName);
  const [skillSearch, setSkillSearch] = useState(_cachedSkillName);
  const [skillOpen, setSkillOpen] = useState(false);
  const skillRef = useRef<HTMLDivElement>(null);

  const [difficulty, setDifficulty] = useState("easy");
  const [questionType, setQuestionType] = useState("mcq");
  const [count, setCount] = useState(5);
  const [countInput, setCountInput] = useState("5");
  const [customPrompt, setCustomPrompt] = useState("");
  const [forceVisuals, setForceVisuals] = useState(false);

  useEffect(() => {
    if (_cachedStandards.length > 0) return;
    const fetchAllStandards = async () => {
      const all: Standard[] = [];
      let offset = 0;
      const limit = 500;
      while (true) {
        const data = await qbFetch<{ items: Standard[]; total: number }>(`/api/proxy/question-bank/standards?limit=${limit}&offset=${offset}`, { ttl: 120_000 });
        all.push(...(data.items || []));
        if (all.length >= data.total || (data.items || []).length < limit) break;
        offset += limit;
      }
      _cachedStandards = all;
      setStandards(all);
      if (all.length > 0) { setStandardCode(all[0].ccss_code); setStandardSearch(all[0].ccss_code); }
      setStandardsLoading(false);
    };
    fetchAllStandards().catch(() => setStandardsLoading(false));
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (standardRef.current && !standardRef.current.contains(e.target as Node)) {
        setStandardOpen(false);
        // Reset search to selected value if user typed but didn't pick
        setStandardSearch(standardCode);
      }
      if (skillRef.current && !skillRef.current.contains(e.target as Node)) {
        setSkillOpen(false);
        setSkillSearch(skillName);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [standardCode, skillName]);

  // Fetch skills when the selected standard changes
  useEffect(() => {
    if (!standardCode) { setSkills([]); return; }
    if (_cachedSkills.length > 0 && _cachedStandardCode === standardCode) return;
    setSkillsLoading(true);
    setSkillName("");
    setSkillSearch("");
    qbFetch<{ items: SkillOption[]; total: number }>(
      skillsUrl(`standard_code=${encodeURIComponent(standardCode)}&limit=200`),
      { ttl: 60_000 },
    )
      .then((data) => setSkills(data.items || []))
      .catch(() => setSkills([]))
      .finally(() => setSkillsLoading(false));
  }, [standardCode]);

  const [generating, setGenerating] = useState(_cachedGenerating);
  const [runId, setRunId] = useState<string | null>(_cachedRunId);
  const [runStatus, setRunStatus] = useState<RunStatus | null>(_cachedRunStatus);
  const [error, setError] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<Record<string, "approved" | "rejected">>(_cachedDecisions);
  const [preview, setPreview] = useState<GeneratedProblem | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync state into module-level cache so it survives tab switches (unmount/remount)
  useEffect(() => {
    _cachedRunId = runId;
    _cachedRunStatus = runStatus;
    _cachedDecisions = decisions;
    _cachedGenerating = generating;
    _cachedStandardCode = standardCode;
    _cachedSkills = skills;
    _cachedSkillName = skillName;
  }, [runId, runStatus, decisions, generating, standardCode, skills, skillName]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const poll = useCallback(async (id: string) => {
    try {
      const res = await fetch(`/api/proxy/question-bank/generate/${id}/status`);
      if (!res.ok) return;
      const data: RunStatus = await res.json();
      setRunStatus(data);
      if (data.run.status === "completed" || data.run.status === "failed") {
        setGenerating(false);
        stopPolling();
      }
    } catch {
      // keep polling on transient errors
    }
  }, [stopPolling]);


  useEffect(() => {
    if (!generating || !runId) return;
    poll(runId);
    pollRef.current = setInterval(() => poll(runId), 3000);
    return stopPolling;
  }, [generating, runId, poll, stopPolling]);

  const handleClearRun = () => {
    stopPolling();
    setRunId(null);
    setRunStatus(null);
    setDecisions({});
    setError(null);
    setGenerating(false);
  };

  const handleGenerate = async () => {
    stopPolling();
    setRunId(null);
    setGenerating(true);
    setRunStatus(null);
    setError(null);
    setDecisions({});

    try {
      const res = await fetch("/api/proxy/question-bank/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: [{
            standard_code: standardCode,
            skill_name: skillName || standardCode,
            grade: standards.find((s) => s.ccss_code === standardCode)?.grade ?? standardCode.split(".")[0] ?? "4",
            difficulty,
            question_type: questionType,
            count,
            custom_prompt: [
              forceVisuals ? "Force a strong visual aid (image_spec) for every problem unless mathematically impossible." : null,
              customPrompt || null,
            ].filter(Boolean).join("\n\n") || null,
          }],
          model: "gpt-5.4",
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Request failed" }));
        throw new Error(err.detail || "Request failed");
      }

      const data = await res.json();
      setRunId(data.run_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed");
      setGenerating(false);
    }
  };

  const handleDecision = async (problemId: string, status: "approved" | "rejected") => {
    setDecisions((d) => ({ ...d, [problemId]: status }));
    await fetch(`/api/proxy/question-bank/generated-problems/${problemId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    invalidateQBCache("/api/proxy/question-bank/generated-problems");
  };

  const handleApproveAllPassed = async () => {
    if (!runStatus) return;
    const passed = runStatus.problems.filter(
      (p) => p.eval_result?.verdict === "pass" && !decisions[p.id],
    );
    for (const p of passed) {
      await handleDecision(p.id, "approved");
    }
  };

  if (standardsLoading) {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-xl font-bold text-neutral-900">AI Generation Engine</h2>
          <p className="mt-0.5 text-sm text-neutral-400">
            Generate original problems from reference examples
          </p>
        </div>
        <div className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          <div className="grid grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-9 w-full rounded-lg" />
              </div>
            ))}
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-14 w-full rounded-lg" />
          </div>
          <div className="flex justify-end">
            <Skeleton className="h-9 w-28 rounded-lg" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-neutral-200 bg-white p-4">
              <Skeleton className="mb-3 h-3.5 w-24" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const problems = runStatus?.problems ?? [];
  const run = runStatus?.run;
  const refProblem = runStatus?.reference_problem ?? null;
  const isComplete = run?.status === "completed" || run?.status === "failed";
  const displayCount = run?.count_generated ?? 0;

  const verdictCounts = problems.reduce(
    (acc, p) => {
      const v = p.eval_result?.verdict || p.status;
      acc[v] = (acc[v] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-neutral-900">AI Generation Engine</h2>
        <p className="mt-0.5 text-sm text-neutral-400">
          Generate original problems from reference examples
        </p>
      </div>

      {/* Config */}
      <div className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
        <div className="grid grid-cols-5 gap-3">
          <div ref={standardRef} className="relative">
            <label className="mb-1 block text-xs font-medium text-neutral-600">Standard Code</label>
            <div className="relative">
                  <input
                    value={standardSearch}
                    onChange={(e) => { setStandardSearch(e.target.value); setStandardOpen(true); }}
                    onFocus={() => { setStandardSearch(""); setStandardOpen(true); }}
                    placeholder="Search standards…"
                    className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 pr-8 text-sm outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
                </div>
                {standardOpen && (() => {
                  const q = standardSearch.toLowerCase();
                  const matches = standards.filter(
                    (s) => s.ccss_code.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)
                  ).slice(0, 50);
                  return matches.length > 0 ? (
                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg">
                      {matches.map((s) => (
                        <button
                          key={s.ccss_code}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setStandardCode(s.ccss_code);
                            setStandardSearch(s.ccss_code);
                            setStandardOpen(false);
                          }}
                          className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-50 ${s.ccss_code === standardCode ? "bg-neutral-100" : ""}`}
                        >
                          <span className="shrink-0 font-mono text-xs font-semibold text-neutral-700">{s.ccss_code}</span>
                          <span className="truncate text-xs text-neutral-500">{s.description}</span>
                        </button>
                      ))}
                    </div>
                  ) : null;
                })()}
          </div>
          <div ref={skillRef} className="relative">
            <label className="mb-1 block text-xs font-medium text-neutral-600">
              Skill {skillsLoading && <Loader2 className="inline h-3 w-3 animate-spin" />}
            </label>
            <div className="relative">
              <input
                value={skillSearch}
                onChange={(e) => { setSkillSearch(e.target.value); setSkillOpen(true); }}
                onFocus={() => { setSkillSearch(""); setSkillOpen(true); }}
                placeholder={skills.length ? "Any skill (optional)" : "No skills"}
                disabled={!standardCode || skills.length === 0}
                className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 pr-8 text-sm outline-none focus:ring-2 focus:ring-neutral-300 disabled:bg-neutral-100 disabled:text-neutral-400"
              />
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
            </div>
            {skillOpen && skills.length > 0 && (() => {
              const q = skillSearch.toLowerCase();
              const matches = skills.filter((s) => s.skill_name.toLowerCase().includes(q)).slice(0, 50);
              return (
                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg">
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); setSkillName(""); setSkillSearch(""); setSkillOpen(false); }}
                    className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-50 ${!skillName ? "bg-neutral-100" : ""}`}
                  >
                    <span className="text-xs italic text-neutral-500">Any skill (use standard only)</span>
                  </button>
                  {matches.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); setSkillName(s.skill_name); setSkillSearch(s.skill_name); setSkillOpen(false); }}
                      className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-50 ${s.skill_name === skillName ? "bg-neutral-100" : ""}`}
                    >
                      <span className="min-w-0 flex-1 truncate text-xs text-neutral-700">{s.skill_name}</span>
                      <span className="shrink-0 text-[10px] text-neutral-400">{s.problem_count}</span>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600">Difficulty</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-neutral-300"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600">Type</label>
            <select
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value)}
              className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-neutral-300"
            >
              <option value="mcq">MCQ</option>
              <option value="free_response">Free Response</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600">Count</label>
            <input
              type="number"
              min={1}
              max={20}
              value={countInput}
              onChange={(e) => {
                setCountInput(e.target.value);
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n) && n >= 1 && n <= 20) setCount(n);
              }}
              onBlur={() => {
                const n = parseInt(countInput, 10);
                if (isNaN(n) || n < 1) { setCount(1); setCountInput("1"); }
                else if (n > 20) { setCount(20); setCountInput("20"); }
                else { setCount(n); setCountInput(String(n)); }
              }}
              className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-neutral-600">Custom Prompt (optional)</label>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            rows={2}
            className="w-full resize-vertical rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-neutral-300"
          />
        </div>
        <div className="flex items-center justify-between">
          <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-neutral-700">
            <input
              type="checkbox"
              checked={forceVisuals}
              onChange={(e) => setForceVisuals(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-neutral-300"
            />
            Force visuals (require an image_spec for every problem)
          </label>
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-1.5 rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-800 disabled:opacity-50"
          >
            <Sparkles size={14} />
            {generating ? `Generating (${displayCount}/${count})…` : `Generate ${count}`}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">{error}</div>
      )}

      {/* Reference vs Generated side-by-side */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-neutral-500">
            <BookOpen size={14} /> Reference
          </div>
          {refProblem ? (
            <div className="qb-safe space-y-2.5 rounded-lg border border-neutral-100 bg-neutral-50 p-3">
              <ProblemText text={refProblem.question_text} className="text-sm font-medium text-neutral-800" />
              {refProblem.answer_choices && refProblem.answer_choices.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {/* key={i}: scraped banks contain duplicate choices, and
                      duplicate keys collapse the siblings. */}
                  {refProblem.answer_choices.map((o, i) => (
                    <span
                      key={i}
                      className={`qb-choice qb-choice-sm rounded-lg px-3 py-1 text-xs font-medium ${
                        o === refProblem.correct_answer
                          ? "bg-neutral-900 text-white"
                          : "border border-neutral-200 bg-white text-neutral-600"
                      }`}
                    >
                      <ProblemText text={o} inline />
                    </span>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-neutral-400">
                {refProblem.id.slice(0, 8)} · {refProblem.source}
              </div>
            </div>
          ) : (
            <div className="flex h-20 items-center justify-center rounded-lg border border-dashed border-neutral-200 bg-neutral-50 text-xs text-neutral-400">
              Reference loads after generation starts
            </div>
          )}
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-neutral-500">
            <Sparkles size={14} /> Generated
          </div>
          {problems.length > 0 ? (
            <div className="qb-safe space-y-2.5 rounded-lg border border-neutral-100 bg-neutral-50 p-3">
              <ProblemText text={problems[0].question_text} className="text-sm font-medium text-neutral-800" />
              <ProblemImages images={problems[0].images} />
              {problems[0].answer_choices && problems[0].answer_choices.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {/* key={i}: see the reference panel above. */}
                  {problems[0].answer_choices.map((o, i) => (
                    <span
                      key={i}
                      className={`qb-choice qb-choice-sm rounded-lg px-3 py-1 text-xs font-medium ${
                        o === problems[0].correct_answer
                          ? "bg-neutral-900 text-white"
                          : "border border-neutral-200 bg-white text-neutral-600"
                      }`}
                    >
                      <ProblemText text={o} inline />
                    </span>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-neutral-400">
                {problems[0].id.slice(0, 8)} · Originality:{" "}
                <span className="font-semibold text-neutral-700">
                  {problems[0].eval_result?.originality.toFixed(2) ?? "—"}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex h-20 items-center justify-center rounded-lg border border-dashed border-neutral-200 bg-neutral-50 text-xs text-neutral-400">
              Generated problem appears here
            </div>
          )}
        </div>
      </div>

      {/* Progress */}
      {generating && (
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-neutral-500" />
              <span className="text-sm font-semibold text-neutral-700">
                Generating {displayCount}/{count}
              </span>
            </div>
            {run?.count_failed && Number(run.count_failed) > 0 ? (
              <span className="text-xs text-red-500">{run.count_failed} failed</span>
            ) : null}
          </div>
          {/* Progress bar */}
          <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full bg-neutral-900 transition-all duration-500"
              style={{ width: `${count > 0 ? (displayCount / count) * 100 : 0}%` }}
            />
          </div>
          {/* Live problems as they arrive */}
          {problems.length > 0 && (
            <div className="space-y-1">
              {problems.map((p) => {
                const verdict = p.eval_result?.verdict || "pending";
                return (
                  <div key={p.id} className="flex items-center gap-2 rounded-lg bg-neutral-50 px-3 py-1.5">
                    <VerdictIcon verdict={verdict} />
                    <span className="min-w-0 flex-1 truncate text-xs text-neutral-600">
                      {problemTextToPreview(p.question_text)}
                    </span>
                    {p.eval_result && (
                      <div className="flex shrink-0 gap-1">
                        <EvalBadge ok={p.eval_result.math} label="M" />
                        <EvalBadge ok={p.eval_result.standard} label="S" />
                        <EvalBadge ok={p.eval_result.difficulty} label="D" />
                      </div>
                    )}
                  </div>
                );
              })}
              {displayCount < count && (
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-neutral-400">
                  <RefreshCw size={12} className="animate-spin" />
                  <span>{count - displayCount} remaining…</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {isComplete && problems.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-neutral-900">Generated Results</h3>
              <p className="text-xs text-neutral-400">
                {problems.length} problems
                {verdictCounts.pass ? <> · <span className="text-emerald-600">{verdictCounts.pass} passed</span></> : null}
                {verdictCounts.review ? <> · <span className="text-amber-600">{verdictCounts.review} review</span></> : null}
                {verdictCounts.fail ? <> · <span className="text-red-600">{verdictCounts.fail} failed</span></> : null}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {verdictCounts.pass ? (
                <button
                  onClick={handleApproveAllPassed}
                  className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                >
                  Approve All Passed
                </button>
              ) : null}
              <button
                onClick={handleClearRun}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-500 transition-colors hover:bg-neutral-50"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
            {problems.map((p, idx) => {
              const decision = decisions[p.id];
              const eval_ = p.eval_result;
              const verdict = eval_?.verdict || p.status;
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-4 px-4 py-3 ${
                    idx < problems.length - 1 ? "border-b border-neutral-100" : ""
                  } ${
                    decision === "approved"
                      ? "bg-emerald-50/50"
                      : decision === "rejected"
                        ? "bg-red-50/30"
                        : ""
                  }`}
                >
                  <VerdictIcon verdict={verdict} />
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-sm font-semibold text-neutral-800">{p.id.slice(0, 8)}</span>
                      <span className="rounded-md border border-neutral-200 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600">
                        {p.standard_code}
                      </span>
                      <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                        p.difficulty_level === "hard"
                          ? "bg-red-50 text-red-700"
                          : p.difficulty_level === "easy"
                            ? "bg-neutral-100 text-neutral-600"
                            : "bg-amber-50 text-amber-700"
                      }`}>
                        {p.difficulty_level}
                      </span>
                      {eval_ && (
                        <span
                          className={`text-[10px] font-medium ${
                            eval_.originality >= 0.8 ? "text-emerald-600" : "text-amber-600"
                          }`}
                        >
                          Originality: {eval_.originality.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs text-neutral-400">
                      {problemTextToPreview(p.question_text)}
                    </p>
                  </div>
                  {eval_ && (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <EvalBadge ok={eval_.math} label="Math" />
                      <EvalBadge ok={eval_.standard} label="Std" />
                      <EvalBadge ok={eval_.difficulty} label="Diff" />
                    </div>
                  )}
                  <div className="flex shrink-0 gap-1">
                    <button
                      className="flex h-7 w-7 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
                      onClick={() => setPreview(p)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    {decision ? (
                      <span
                        className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${
                          decision === "approved"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {decision === "approved" ? "Approved" : "Rejected"}
                      </span>
                    ) : (
                      <>
                        <button
                          className="flex h-7 w-7 items-center justify-center rounded-md text-emerald-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                          onClick={() => handleDecision(p.id, "approved")}
                        >
                          <ThumbsUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          className="flex h-7 w-7 items-center justify-center rounded-md text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                          onClick={() => handleDecision(p.id, "rejected")}
                        >
                          <ThumbsDown className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isComplete && problems.length === 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white p-4 text-sm text-neutral-400">
          Generation completed but no problems were produced. Check Lambda logs.
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-h-[90vh] w-[95vw] overflow-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Problem Preview</DialogTitle>
            {preview && (
              <p className="mt-0.5 text-xs text-neutral-400">
                {preview.standard_code} · {preview.difficulty_level} · {preview.question_type}
              </p>
            )}
          </DialogHeader>
          {preview && (
            <div className="qb-safe space-y-3">
            {preview.surveyjs_config ? (
              // SurveyRenderer places the figure itself.
              <Suspense fallback={<div className="py-4 text-center text-sm text-neutral-400">Loading preview...</div>}>
                <SurveyRenderer config={preview.surveyjs_config} images={preview.images} />
              </Suspense>
            ) : (
              <div className="space-y-3">
                <ProblemImages images={preview.images} />
                <ProblemText text={preview.question_text} className="whitespace-pre-wrap text-base leading-relaxed" />
                {preview.answer_choices && preview.answer_choices.length > 0 && (
                  <div className="space-y-1.5">
                    {preview.answer_choices.map((o, i) => (
                      <div
                        key={i}
                        className={`qb-choice flex items-center gap-3 rounded-lg px-4 py-2.5 ${
                          o === preview.correct_answer
                            ? "border-2 border-neutral-900 bg-neutral-50"
                            : "border border-neutral-200"
                        }`}
                      >
                        <ProblemText text={o} className="text-sm" inline />
                      </div>
                    ))}
                  </div>
                )}
                {/* qb-choice only when there are choices to line up with —
                    see ExtractionEngine. */}
                {preview.correct_answer && (
                  <div
                    className={`qb-safe ${
                      preview.answer_choices?.length ? "qb-choice" : "qb-answer"
                    } rounded-lg border border-neutral-200 bg-neutral-50 p-3`}
                  >
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-neutral-400">Correct Answer</div>
                    <ProblemText text={String(preview.correct_answer)} className="text-sm font-bold" inline />
                  </div>
                )}
              </div>
            )
            }
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
